import type { ReactNode } from "react";
import Tag, { type TagVariant } from "./tag";
import "./section-heading.css";

type Lines = string | readonly string[];

type SectionHeadingProps = {
  tag: string;
  tagVariant: TagVariant;
  /** An array keeps the mockup's line breaks where the column is wide enough (above 900 px). */
  title: Lines;
  lead?: Lines;
  align?: "left" | "center";
  /** The surface under the heading: light (ink text) or dark, a photo or night (white text). */
  tone?: "light" | "dark";
  /** Adds .v2-reveal to each part (tag, title, lead, actions: --i 0 to 3), for use inside <Reveal>. */
  reveal?: boolean;
  className?: string;
  /** Actions, 40 px under the lead. */
  children?: ReactNode;
};

function renderLines(lines: Lines) {
  if (typeof lines === "string") return lines;
  return lines.map((line, i) => (
    <span key={line} className="ds-heading__line">
      {i > 0 && " "}
      {line}
    </span>
  ));
}

/** Tag, h2 and lead: the opening of a homepage section. */
export default function SectionHeading({
  tag,
  tagVariant,
  title,
  lead,
  align = "left",
  tone = "light",
  reveal = false,
  className,
  children,
}: SectionHeadingProps) {
  const part = (name: string) => `ds-heading__${name}${reveal ? " v2-reveal" : ""}`;
  return (
    <div className={`ds-heading ds-heading--${align} ds-heading--${tone}${className ? ` ${className}` : ""}`}>
      <Tag variant={tagVariant} className={part("tag")}>
        {tag}
      </Tag>
      <h2 className={part("title")}>{renderLines(title)}</h2>
      {lead && <p className={part("lead")}>{renderLines(lead)}</p>}
      {children && <div className={part("actions")}>{children}</div>}
    </div>
  );
}
